/**
 * @ Author: Housefolios
 * @ Create Time: 2021-10-05 11:07:32
 * @ Modified by: David Helmick
 * @ Modified time: 2025-05-09 10:18:50
 * @ Description: Dialog to share a marketplace through email.
 */

import React, { useState } from 'react'
import ShareIcon from '@mui/icons-material/Share'
import {
  Grid,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  MenuItem,
  TextField,
  Typography,
  Box,
} from '@mui/material'

// Centralized lodash imports - much cleaner!
import {
  map,
  head,
  isEmpty,
  includes,
  toLower,
  get,
  find,
} from '@/utils/lodash'

import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import { GET_ORGS_MARKETPLACE_SHARED_WITH } from '../queries'
import { useMutation, useQuery } from '@apollo/client/react'
import { SHARE_MARKETPLACE } from '../mutations'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import RemoveSharedMarketplace from '../RemoveSharedMarketplace'
import { GET_AFFILIATES } from '@/housefolios-components/CreateAccount/CreateAccountCard/queries'
import { GET_ACTIVE_ORGS } from '@/housefolios-components/ReportsComponents/Reports/queries'
import UpdateSharedMarketplace from '../UpdateSharedMarketplace'
import { formatDisplayDate } from '@/utils/date'
import { useShareEmails } from '@/hooks/useShareEmails'
import ShareEmailsAutocomplete from '@/housefolios-components/FormComponents/ShareEmailsAutocomplete'
import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'

function ShareMarketplaceComponent(props) {
  const { sidebarMenu, marketplace = {}, activeOrg } = props
  const [shareType, setShareType] = useState('email')
  const shareEmails = useShareEmails()
  const [sharePermission, setSharePermission] = useState('viewOnly')
  const [openShareMarketplace, setOpenShareMarketplace] =
    useState(false)
  const [confirmAffiliateShare, setConfirmAffiliateShare] =
    useState(false)
  const [loader, setLoader] = useState(false)
  const handleOpenShareMarketplace = () => {
    setOpenShareMarketplace(true)
    shareEmails.resetEmails()
  }
  const handleCloseShareMarketplace = () => {
    setOpenShareMarketplace(false)
    setLoader(false)
    setConfirmAffiliateShare(false)
  }

  const [shareMarketplace] = useMutation(SHARE_MARKETPLACE)

  const { loading, error, data } = useQuery(
    GET_ORGS_MARKETPLACE_SHARED_WITH,
    {
      variables: {
        url: marketplace.url,
      },
    },
  )
  const {
    loading: orgLoading,
    error: orgError,
    data: orgData,
  } = useQuery(GET_ACTIVE_ORGS)

  if (loading || orgLoading) return <LoadingSkeleton variant="menuItem" />
  if (error) return `Error! ${error.message}`
  if (orgError) return `Error! ${orgError.message}`
  const { orgsMarketplaceSharedWith } = data
  const { activeOrgs = [] } = orgData || {}

  return (
    <>
      {sidebarMenu && marketplace.status === 'active' && (
        <MenuItem onClick={handleOpenShareMarketplace}>
          <ShareIcon style={{ marginRight: 5, color: '#757575' }} />
          <span>Share Marketplace</span>
        </MenuItem>
      )}

      <Dialog
        open={openShareMarketplace}
        onClose={handleCloseShareMarketplace}
        maxWidth="sm"
        fullWidth={true}
        scroll="body"
      >
        <DialogTitle>
          Share {marketplace.name} marketplace
        </DialogTitle>
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={
            <Box sx={{ color: '#368cff' }}>Sharing Marketplace</Box>
          }
        >
          <ValidatorForm
            onSubmit={(event) => {
              event.preventDefault()
              if (shareType === 'affiliate') {
                setConfirmAffiliateShare(true)
                return
              }
              const finalEmails = shareEmails.collectEmails()
              if (isEmpty(finalEmails)) {
                enqueueSnackbar(
                  'Enter at least one valid email address',
                  {
                    variant: 'warning',
                    autoHideDuration: 4000,
                  },
                )
                return
              }
              setLoader(true)
              shareMarketplace({
                variables: {
                  url: marketplace.url,
                  emails: finalEmails,
                  sharePermission,
                },
              })
                .then(() => {
                  enqueueSnackbar(`Email Sent`, {
                    variant: 'success',
                    autoHideDuration: 3000,
                  })
                  setConfirmAffiliateShare(false)
                  handleCloseShareMarketplace()
                })
                .catch((error) => {
                  enqueueSnackbar(`Failed to send email`, {
                    variant: 'error',
                    autoHideDuration: 3000,
                  })
                  setLoader(false)
                  setConfirmAffiliateShare(false)
                })
            }}
            style={{
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DialogContent>
              <Grid container direction="row">
                {props.session.me.activeOrg.isAffiliate && (
                  <Grid size={12}>
                    <TextField
                      select
                      name="shareType"
                      variant="outlined"
                      margin="dense"
                      label="Share Type"
                      fullWidth={true}
                      // style={{ width: 175 }}
                      value={shareType}
                      onChange={(event) =>
                        setShareType(event.target.value)
                      }
                      required={true}
                    >
                      <MenuItem key={'email'} value={'email'}>
                        {'Share By Email'}
                      </MenuItem>
                      <MenuItem key={'affiliate'} value={'affiliate'}>
                        {'Share By Affiliate'}
                      </MenuItem>
                    </TextField>
                  </Grid>
                )}
                {shareType === 'email' && (
                  <Grid size={12}>
                    <ShareEmailsAutocomplete
                      shareEmails={shareEmails}
                    />
                    {/* <TextValidator
                                                          name="email"
                                                          type="email"
                                                          variant='outlined'
                                                          margin='dense'
                                                          autoFocus
                                                          onChange={event => setEmail(event.target.value)}
                                                          value={email}
                                                          placeholder="Enter email address..."
                                                          label="Email"
                                                          fullWidth={true}
                                                          validators={[
                                                          'required',
                                                          'maxStringLength: 50',
                                                          'isEmail',
                                                          ]}
                                                          errorMessages={[
                                                          'This field is required',
                                                          'You cannot exceed 50 characters',
                                                          'Not a valid email address',
                                                          ]}
                                                      /> */}
                  </Grid>
                )}
                <Grid size={12}>
                  <TextField
                    select
                    name="sharePermission"
                    variant="outlined"
                    margin="dense"
                    label="Permission"
                    fullWidth={true}
                    // style={{ width: 175 }}
                    value={sharePermission}
                    onChange={(event) =>
                      setSharePermission(event.target.value)
                    }
                    required={true}
                  >
                    <MenuItem key={'viewOnly'} value={'viewOnly'}>
                      {'View Only'}
                    </MenuItem>
                    <MenuItem
                      key={'canListDelist'}
                      value={'canListDelist'}
                    >
                      {'Can List - Needs Approval'}
                    </MenuItem>
                    <MenuItem
                      key={'canListDelistAutoApprove'}
                      value={'canListDelistAutoApprove'}
                    >
                      {'Can List - No Approval'}
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid size={12} style={{ padding: 10 }}>
                  {!isEmpty(orgsMarketplaceSharedWith) && (
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Typography>Shared With</Typography>
                      </Grid>
                      {map(orgsMarketplaceSharedWith, (org) => (
                        <Grid size={12} key={org._id || org.email || org.name}>
                          <Grid
                            container
                            direction="row"
                            spacing={2}
                            sx={{
                              alignItems: 'center',
                            }}
                          >
                            <Grid>
                              <Avatar>{head(org.name)}</Avatar>
                            </Grid>
                            <Grid>
                              <Grid container direction="column" sx={{ lineHeight: "1.3" }}>
                                <Grid>
                                  <Box component="b" sx={{ fontSize: "1.15rem !important" }}>
                                    {org.name}:
                                  </Box>{' '}
                                  <Box
                                    component="span"
                                    sx={{
                                      color: 'rgba(7, 9, 25, 0.5)',
                                      display: 'block',
                                    }}
                                  >
                                    {formatDisplayDate(
                                      get(
                                        find(org.sharedMarketplaces, [
                                          'url',
                                          marketplace.url,
                                        ]),
                                        'shareDate',
                                      ),
                                    )}
                                  </Box>{' '}
                                </Grid>
                                <Grid>
                                  <Box
                                    component="span"
                                    sx={{
                                      color: 'rgba(7, 9, 25, 0.5)',
                                      display: 'block',
                                    }}
                                  >
                                    {org.email}
                                  </Box>
                                </Grid>
                                <Grid>
                                  <Box
                                    component="span"
                                    sx={{
                                      color: 'rgba(7, 9, 25, 0.5)',
                                      display: 'block',
                                    }}
                                  >
                                    {get(
                                      find(org.sharedMarketplaces, [
                                        'url',
                                        marketplace.url,
                                      ]),
                                      'sharePermission',
                                    ) === 'canListDelistAutoApprove'
                                      ? 'Can List - No Approval'
                                      : get(
                                            find(
                                              org.sharedMarketplaces,
                                              [
                                                'url',
                                                marketplace.url,
                                              ],
                                            ),
                                            'sharePermission',
                                          ) === 'canListDelist'
                                        ? 'Can List - Needs Approval'
                                        : 'View Only'}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Typography style={{ flex: 1 }} />
                            <Grid>
                              <UpdateSharedMarketplace
                                orgId={org._id}
                                marketplaceURL={marketplace.url}
                                shareAccess
                                sharePermission={get(
                                  find(org.sharedMarketplaces, [
                                    'url',
                                    marketplace.url,
                                  ]),
                                  'sharePermission',
                                )}
                                canList={
                                  get(
                                    find(org.sharedMarketplaces, [
                                      'url',
                                      marketplace.url,
                                    ]),
                                    'sharePermission',
                                  ) === 'canListDelist'
                                }
                              />
                            </Grid>
                            <Grid>
                              <RemoveSharedMarketplace
                                orgId={org._id}
                                marketplaceURL={marketplace.url}
                                shareAccess
                                canList={
                                  get(
                                    find(org.sharedMarketplaces, [
                                      'url',
                                      marketplace.url,
                                    ]),
                                    'sharePermission',
                                  ) === 'canListDelist'
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                variant="text"
                size="small"
                onClick={handleCloseShareMarketplace}
              >
                <Typography>CANCEL</Typography>
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                type="submit"
                disabled={loader}
              >
                <Typography>
                  {shareType === 'affiliate'
                    ? `SHARE WITH ${activeOrgs.length} ORGS`
                    : 'SHARE'}
                </Typography>
              </Button>
            </DialogActions>
          </ValidatorForm>
        </BlockUi>
      </Dialog>
      <Dialog
        open={confirmAffiliateShare}
        onClose={() => setConfirmAffiliateShare(false)}
      >
        <DialogTitle>Email every active organization?</DialogTitle>
        <DialogContent>
          <Typography>
            Share By Affiliate will send this marketplace to{' '}
            {activeOrgs.length} active organization
            {activeOrgs.length === 1 ? '' : 's'}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAffiliateShare(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmAffiliateShare(false)
              setLoader(true)
              shareMarketplace({
                variables: {
                  url: marketplace.url,
                  emails: map(activeOrgs, (org) => org.email),
                  sharePermission,
                },
              })
                .then(() => {
                  enqueueSnackbar(`Email Sent`, {
                    variant: 'success',
                    autoHideDuration: 3000,
                  })
                  handleCloseShareMarketplace()
                })
                .catch(() => {
                  enqueueSnackbar(`Failed to send email`, {
                    variant: 'error',
                    autoHideDuration: 3000,
                  })
                  setLoader(false)
                })
            }}
            disabled={loader}
          >
            Send to all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
export default withSession(ShareMarketplaceComponent)
